import type { ProviderPageAd } from './providers/shared/provider-page-ad'
import { AdPlacementType, type AdEventListenerArgs, type AdEventType, type PageAdType, type VideoAdType } from './types'
import { AdConsoleLogger } from './utils/ad-console-logger'

export enum PageAdState {
    'loading' = 'loading',
    'empty' = 'empty',
    'showing' = 'showing',
}

export class PageAd extends EventTarget {
    readonly type = AdPlacementType.IN_PAGE

    readonly placement: PageAdType | VideoAdType

    readonly id: string

    state: PageAdState = PageAdState.loading

    #intermediateInternalBanner: ProviderPageAd | undefined
    #internalBanner: ProviderPageAd | undefined

    #containerEl: HTMLElement | undefined

    #destroyed = false

    readonly #onRefresh: (() => void) | undefined

    #onDestroy: () => void

    readonly #adConsoleLogger: AdConsoleLogger

    constructor({
        placement,
        onDestroy,
        onRefresh,
    }: {
        placement: PageAdType | VideoAdType
        onDestroy: () => void
        onRefresh?: () => void
    }) {
        super()

        this.placement = placement
        this.id = crypto.randomUUID()
        this.#onDestroy = onDestroy
        this.#onRefresh = onRefresh
        this.#adConsoleLogger = new AdConsoleLogger('public-banner', placement)
    }

    private dispatch<T>(event: AdEventType, payload?: T) {
        this.dispatchEvent(new CustomEvent(event, { detail: payload }))
    }

    override addEventListener(...args: AdEventListenerArgs) {
        super.addEventListener(...args)
    }

    override removeEventListener(...args: AdEventListenerArgs) {
        super.removeEventListener(...args)
    }

    setIntermediateInternalBanner(banner: ProviderPageAd) {
        this.#intermediateInternalBanner = banner
        // Every provider banner passes through here before the waterfall settles, so one listener
        // covers a no-fill on the first render as well as every later SDK refresh.
        banner.addEventListener('native_no_fill', (e) => this.dispatch('native_no_fill', e.detail))
    }

    setInternalBanner(banner: ProviderPageAd, container: HTMLElement) {
        this.#intermediateInternalBanner = undefined

        this.#internalBanner?.destroy()
        this.#internalBanner = banner

        this.#containerEl?.remove()
        this.#containerEl = container

        banner.addEventListener('viewable', () => this.triggerViewable())
        banner.addEventListener('request', () => this.triggerRequest())
        banner.addEventListener('closed', () => this.triggerClosed(), { once: true })
    }

    get containerElId(): string | undefined {
        return this.#containerEl?.id
    }

    updatePosition() {
        const banner = this.#internalBanner ?? this.#intermediateInternalBanner
        banner?.updatePosition()
    }

    destroy() {
        if (this.#destroyed) {
            return
        }
        this.#destroyed = true

        this.#onDestroy()

        this.#intermediateInternalBanner?.destroy()
        this.#intermediateInternalBanner = undefined

        this.#internalBanner?.destroy()
        this.#internalBanner = undefined

        this.#containerEl?.remove()
        this.#containerEl = undefined

        this.dispatch('destroyed')
    }

    triggerEmpty(reason?: 'timeout' | 'unfilled' | 'no_container' | 'validation-error') {
        if (this.state !== PageAdState.empty) {
            this.#adConsoleLogger.debug(`${this.state} → empty`, { reason })
            this.state = PageAdState.empty
            this.dispatch('empty', { reason })
        }
    }

    triggerRequest() {
        this.dispatch('request')
    }

    triggerViewable() {
        this.#adConsoleLogger.debug('viewable')
        this.dispatch('viewable')
    }

    triggerRendered() {
        this.#adConsoleLogger.debug(`${this.state} → showing`)
        this.state = PageAdState.showing
        this.dispatch('rendered')
    }

    triggerClosed() {
        this.#adConsoleLogger.debug(`${this.state} → closed`)
        this.dispatch('closed')
        this.destroy()
    }

    refresh() {
        if (this.state !== PageAdState.loading) {
            this.state = PageAdState.loading
            this.#onRefresh?.()
        }
    }
}
